import { inject, InjectionToken, ResourceRef, Signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  ApiService,
  GeneralInfo,
  ParcelsWithProperties,
} from '@core/api/angular';
import { getParcelsPfiList } from '@core/api/angular/operations/get-parcels-pfi-list';
import { searchForParcelByViewPfi } from '@core/api/angular/operations/search-for-parcel-by-view-pfi';
import { IdentifyService } from '@lassi/webapp-shared';
import { type GeoJSON, Point } from 'leaflet';
import { defaultIfEmpty, forkJoin, map, switchMap } from 'rxjs';

export const IDENTIFY_PARCEL_RESOURCE_FACTORY = new InjectionToken(
  'IDENTIFY_PARCEL_RESOURCE_FACTORY',
  {
    factory: () => {
      const api = inject(ApiService);
      const identifyService = inject(IdentifyService);

      return (vPfis: Signal<string[]>, clicked: Signal<Point | null>) =>
        createIdentifyPropertyResource(vPfis, clicked, api, identifyService);
    },
  },
);

function isMultiPolygon(geometry: unknown): geometry is GeoJSON.MultiPolygon {
  return (
    (geometry as GeoJSON.MultiPolygon).type === 'MultiPolygon' &&
    Array.isArray((geometry as GeoJSON.MultiPolygon).coordinates)
  );
}

export interface IParcelsWithPropertiesAndGeometry
  extends ParcelsWithProperties {
  geometry: GeoJSON.MultiPolygon;
}

function createIdentifyPropertyResource(
  vPfis: Signal<string[]>,
  clicked: Signal<Point | null>,
  api: ApiService,
  identifyService: IdentifyService,
): ResourceRef<
  | {
      general: GeneralInfo;
      parcels: IParcelsWithPropertiesAndGeometry[];
      geometry: GeoJSON.MultiPolygon;
      coords: Point;
    }
  | undefined
> {
  return rxResource({
    params: () => ({ vPfis: vPfis(), clicked: clicked() }),
    stream: ({ params: { vPfis, clicked } }) => {
      return forkJoin(
        vPfis.map((vPfi) =>
          api
            .invoke(searchForParcelByViewPfi, {
              viewPfi: vPfi,
            })
            .pipe(
              map((shapes) => {
                if (isMultiPolygon(shapes.geometry?.shapes[0].geojson)) {
                  return {
                    vPfi,
                    geometry: shapes.geometry?.shapes[0].geojson,
                  };
                }
                throw new Error('Unexpected geometry type');
              }),
            ),
        ),
      ).pipe(
        defaultIfEmpty([]),
        map((result) => result.flat()),
        switchMap((shapes) => {
          let coords = clicked;

          if (!coords) {
            // if no clicked location, use the centroid of the outer ring of the first polygon
            // not using a proper centroid calculation for simplicity, but that would be more accurate (but would need to reproject first etc)
            const outerRing = shapes[0].geometry.coordinates[0][0];
            let sumX = 0,
              sumY = 0;

            for (const [x, y] of outerRing) {
              sumX += x;
              sumY += y;
            }

            const midX = sumX / outerRing.length;
            const midY = sumY / outerRing.length;

            coords = new Point(midX, midY);
          }

          const geomMap = shapes.reduce(
            (acc, { vPfi, geometry }) => {
              acc[vPfi] = geometry;
              return acc;
            },
            {} as Record<string, GeoJSON.MultiPolygon>,
          );

          return forkJoin({
            general: identifyService.identifyGeneralInformation(coords),
            parcels: forkJoin(
              vPfis.map((vPfi) =>
                api
                  .invoke(getParcelsPfiList, {
                    vPfi,
                  })
                  .pipe(
                    switchMap((pfis) =>
                      identifyService
                        .identifyParcels(pfis.parcels.map((p) => Number(p)))
                        .pipe(
                          map((parcels) =>
                            parcels.map((parcel) => ({
                              ...parcel,
                              geometry: geomMap[vPfi],
                            })),
                          ),
                        ),
                    ),
                  ),
              ),
            ).pipe(
              defaultIfEmpty([]),
              map((res) => res.flat()),
            ),
            geometry: Promise.resolve({
              type: 'MultiPolygon' as const,
              coordinates: shapes.flatMap(
                (shape) => shape.geometry.coordinates,
              ),
            }),
            coords: Promise.resolve(coords),
          });
        }),
      );
    },
  });
}
